import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { ClientLocationPlanBaseData } from "api-types/locationPlans/locationPlansResponses";
import type { ClientLotCommunityPlanHydrated } from "api-types/lotCommunityPlans/lotCommunityPlansResponses";
import type { ClientLot, ClientLotHydrated } from "api-types/lots/lotsResponses";
import type { ClientMasterPlanHydrated } from "api-types/masterPlans/masterPlansResponses";
import type { PlanType } from "api-types/modelTypes";
import type { Doc } from "building-types/doc";
import type { ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { Provider } from "react-redux";
import Loading from "ui/components/Loading";
import PlanLibraryDiscardAllOverridesModal from "./modal/PlanLibraryDiscardAllOverridesModal";
import { planLibraryActions } from "./planLibraryReducer";
import { planLibraryStore, usePlanLibraryDispatch, usePlanLibrarySelector } from "./planLibraryStore";
import { PlanLibraryStateType } from "./planLibraryTypes";

interface PlanLibraryProps {
  planId: string;
  planType: PlanType;
  children: ReactNode;
  isReadOnly: boolean;

  publishedDoc?: Doc;
  masterPlan?: ClientMasterPlanHydrated;
  builder: ClientBuilder;

  locationPlanBaseData?: ClientLocationPlanBaseData;
  lotCommunityPlan?: ClientLotCommunityPlanHydrated;
  lot?: ClientLotHydrated;
}

function PlanLibraryInternal({
  planId,
  planType,
  children,
  isReadOnly,

  publishedDoc,
  masterPlan,
  builder,

  locationPlanBaseData,
  lotCommunityPlan,
  lot,
}: PlanLibraryProps) {
  const { discardAllOverrides, newPlanId, dataState } = usePlanLibrarySelector((state) => ({
    discardAllOverrides: state.discardAllOverrides,
    newPlanId: state.planId,
    dataState: state.state,
  }));
  const dispatch = usePlanLibraryDispatch();

  // Initialize the plan library state, only needs to happen once when all the data is available.
  // We can combine the init for all plan types into one since we are only showing one plan type at a time.
  useEffect(() => {
    if (dataState === PlanLibraryStateType.INITIALIZED && newPlanId === planId) {
      return;
    }
    switch (planType) {
      case "master_plan":
        if (publishedDoc && masterPlan && builder) {
          dispatch(planLibraryActions.initForMasterPlan({ publishedDoc, masterPlan, builder, isReadOnly }));
        }
        break;
      case "community_plan":
        // Hidden bug: locationPlanBaseData gets reset on refocus for some reason.
        if (locationPlanBaseData && builder && locationPlanBaseData.location) {
          dispatch(
            planLibraryActions.initForLocationPlan({
              locationPlan: locationPlanBaseData.locationPlan,
              masterPlan: locationPlanBaseData.masterPlan,
              location: locationPlanBaseData.location,
              optionSets: locationPlanBaseData.optionSets,
              optionEffects: locationPlanBaseData.optionEffects,
              builder,
              isReadOnly,
            }),
          );
        }
        break;
      case "lot":
        if (lotCommunityPlan && locationPlanBaseData && builder && lot && locationPlanBaseData.location) {
          dispatch(
            planLibraryActions.initForLotCommunityPlan({
              lotCommunityPlan,
              locationPlan: locationPlanBaseData.locationPlan,
              masterPlan: locationPlanBaseData.masterPlan,
              location: locationPlanBaseData.location,
              builder,
              // This is an ugly hack, but will be fixed when we migrate fully to the new database types.
              lot: lot as ClientLot,
              optionSets: locationPlanBaseData.optionSets,
              optionEffects: locationPlanBaseData.optionEffects,
              isReadOnly,
            }),
          );
        }
        break;
      default:
        break;
    }
  }, [planType, publishedDoc, masterPlan, builder, locationPlanBaseData, lotCommunityPlan, lot, dataState]);

  if (planId === newPlanId) {
    // planId === newPlanId if initForMasterPlan/initForCommunityPlan/initForLotCommunityPlan action has completed
    return (
      <>
        {discardAllOverrides && <PlanLibraryDiscardAllOverridesModal />}
        {children}
      </>
    );
  }

  return <Loading />;
}

export default function PlanLibrary(props: PlanLibraryProps) {
  const store = useMemo(() => planLibraryStore, []);
  return (
    <Provider store={store}>
      <PlanLibraryInternal {...props} />
    </Provider>
  );
}
